import jsPDF from 'jspdf';

/* ─── Color Palette ─────────────────────────────────────── */
const PALETTE = ['#6ec7c8', '#f5c542', '#ff66c4', '#c164d8', '#4ecdc4', '#ffc2c2', '#ffd166', '#f78fb3'];
const COL_TEAL    = [110, 199, 200];
const COL_DARK    = [17,  24,  39];
const COL_WHITE   = [255, 255, 255];
const COL_GRAY1   = [249, 250, 251];
const COL_GRAY2   = [229, 231, 235];
const COL_GRAY3   = [156, 163, 175];
const COL_GRAY4   = [107, 114, 128];
const COL_GRAY5   = [75,  85,  99];
const COL_GRAY6   = [55,  65,  81];
const COL_GOLD    = [245, 197, 66];
const COL_GREEN   = [6,   95,  70];
const COL_GREEN_L = [236, 253, 245];
const COL_AMBER   = [146, 64,  14];
const COL_AMBER_L = [255, 251, 235];

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
function rgb(arr) { return arr; }

/* ─── Helpers ───────────────────────────────────────────── */
function fmtHours(hours) {
  if (!hours || isNaN(hours) || hours <= 0) return '0 min';
  const m = Math.round(hours * 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

function fmtDate(dateVal) {
  if (!dateVal) return '--';
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return String(dateVal);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '--'; }
}

function trunc(str, maxLen) {
  if (str === null || str === undefined) return '--';
  const s = String(str);
  return s.length > maxLen ? s.slice(0, maxLen - 2) + '..' : s;
}

function setFont(pdf, style, size, colorArr) {
  pdf.setFont('helvetica', style);
  pdf.setFontSize(size);
  if (colorArr) pdf.setTextColor(...colorArr);
}

/* ─── HEADER (premium redesign) ─────────────────────────── */
/*
  Layout (height 32mm):
  ┌──────────────────────────────────────────────────────────┐
  │  Teal accent bar (left 3mm)        | Teal accent bar top │
  │  OLEANDER FARMS RESORT & SPA       | [PAGE BADGE]        │
  │  Operational Intelligence Report   | Generated: ...      │
  │  ────────────── divider line ───────────────────────────  │
  │  Filter Period:  <filterText>                            │
  └──────────────────────────────────────────────────────────┘
*/
function drawHeader(pdf, pageTitle, filterText, pageNum, totalPages, generatedAt) {
  const W = 210;
  const hTop = 6;     // top of header block
  const hH   = 30;    // height of header block
  const MARGIN = 8;

  // Main dark background
  pdf.setFillColor(...COL_DARK);
  pdf.roundedRect(MARGIN, hTop, W - MARGIN * 2, hH, 2.5, 2.5, 'F');

  // Left teal accent bar
  pdf.setFillColor(...COL_TEAL);
  pdf.roundedRect(MARGIN, hTop, 3.5, hH, 1.5, 1.5, 'F');
  // square off the right edge of the left bar
  pdf.rect(MARGIN + 1.5, hTop, 2, hH, 'F');

  // Hotel name
  setFont(pdf, 'bold', 12, COL_WHITE);
  pdf.text('OLEANDER FARMS', MARGIN + 7.5, hTop + 8.5);

  // Report subtitle (section name)
  setFont(pdf, 'normal', 7.5, COL_GRAY3);
  pdf.text('Operational Intelligence Report', MARGIN + 7.5, hTop + 13.5);

  // Thin horizontal separator
  pdf.setDrawColor(...COL_GRAY4);
  pdf.setLineWidth(0.2);
  pdf.line(MARGIN + 7, hTop + 15.5, W - MARGIN * 2 - 4, hTop + 15.5);

  // Filter period label
  setFont(pdf, 'bold', 6.5, COL_TEAL);
  pdf.text('FILTER:', MARGIN + 7.5, hTop + 20.5);
  setFont(pdf, 'normal', 6.5, [180, 220, 220]);
  pdf.text(trunc(filterText, 72), MARGIN + 20, hTop + 20.5);

  // Section / page badge (right pill)
  const badgeW = 58;
  const badgeX = W - MARGIN - badgeW;
  pdf.setFillColor(...COL_TEAL);
  pdf.roundedRect(badgeX, hTop + 4, badgeW, 9, 2, 2, 'F');
  setFont(pdf, 'bold', 7, COL_DARK);
  pdf.text(`${pageTitle}  |  Page ${pageNum} / ${totalPages}`, badgeX + badgeW / 2, hTop + 9.5, { align: 'center' });

  // Generated at (below badge)
  setFont(pdf, 'normal', 6, COL_GRAY4);
  pdf.text(`Generated: ${generatedAt}`, badgeX + badgeW / 2, hTop + 16, { align: 'center' });

  // Right decorative corner dots pattern
  [0, 4, 8].forEach(dx => {
    [0, 4, 8].forEach(dy => {
      pdf.setFillColor(110, 199, 200, 40);
      pdf.circle(badgeX - 4 - dx, hTop + 23 + dy, 0.5, 'F');
    });
  });
}

/* ─── FOOTER ─────────────────────────────────────────────── */
function drawFooter(pdf, pageLabel, filterText, H) {
  const W = 210;
  const MARGIN = 8;
  const footY = H - 8;

  // Light footer bar
  pdf.setFillColor(248, 250, 252);
  pdf.rect(MARGIN, footY - 3, W - MARGIN * 2, 6, 'F');

  setFont(pdf, 'normal', 6, COL_GRAY4);
  pdf.text('Oleander Farms', MARGIN + 2, footY + 0.5);

  setFont(pdf, 'normal', 6, COL_GRAY3);
  pdf.text(pageLabel, W / 2, footY + 0.5, { align: 'center' });

  // Teal dot on right
  pdf.setFillColor(...COL_TEAL);
  pdf.circle(W - MARGIN - 3, footY + 0.5, 1.5, 'F');

  // Top rule
  pdf.setDrawColor(...COL_GRAY2);
  pdf.setLineWidth(0.25);
  pdf.line(MARGIN, footY - 3, W - MARGIN, footY - 3);
}

/* ─── SECTION TITLE ──────────────────────────────────────── */
function drawSectionTitle(pdf, title, subtitle, y, x = 8) {
  // Small teal left accent
  pdf.setFillColor(...COL_TEAL);
  pdf.rect(x, y - 2.5, 2, 7, 'F');

  setFont(pdf, 'bold', 8.5, COL_DARK);
  pdf.text(title, x + 4, y + 2);

  if (subtitle) {
    setFont(pdf, 'normal', 6.5, COL_GRAY4);
    pdf.text(subtitle, x + 4, y + 6.5);
    return y + 9;
  }
  return y + 6;
}

/* ─── KPI CHIP ───────────────────────────────────────────── */
function drawKpiChip(pdf, label, value, x, y, w, h, bgArr, accentArr) {
  // Card bg
  pdf.setFillColor(...bgArr);
  pdf.roundedRect(x, y, w, h, 2, 2, 'F');

  // Top accent stripe
  pdf.setFillColor(...accentArr);
  pdf.roundedRect(x, y, w, 2.5, 1, 1, 'F');
  pdf.rect(x, y + 1.5, w, 1, 'F'); // square bottom of stripe

  // Label
  setFont(pdf, 'normal', 5.5, COL_GRAY4);
  pdf.text(label.toUpperCase(), x + w / 2, y + 6, { align: 'center' });

  // Value
  setFont(pdf, 'bold', 13, COL_DARK);
  pdf.text(String(value ?? '--'), x + w / 2, y + h - 3.5, { align: 'center' });
}

/* ─── STATS BAR (no emoji!) ──────────────────────────────── */
function drawStatsBar(pdf, stats, x, y, w, h) {
  // Dark pill background
  pdf.setFillColor(...COL_DARK);
  pdf.roundedRect(x, y, w, h, 2, 2, 'F');

  // Draw each stat
  const slotW = w / stats.length;
  stats.forEach((s, i) => {
    const sx = x + i * slotW + slotW / 2;
    const [lr, lg, lb] = hexToRgb(s.color || '#6ec7c8');

    // Small colored indicator dot
    pdf.setFillColor(lr, lg, lb);
    pdf.circle(sx - slotW / 2 + 5, y + h / 2, 1.5, 'F');

    // Label
    setFont(pdf, 'normal', 5.5, [lr, lg, lb]);
    pdf.text(s.label, sx - slotW / 2 + 9, y + h / 2 - 1.5);

    // Value (bigger, white)
    setFont(pdf, 'bold', 7.5, COL_WHITE);
    pdf.text(String(s.value), sx - slotW / 2 + 9, y + h / 2 + 3);

    // Divider (except last)
    if (i < stats.length - 1) {
      pdf.setDrawColor(55, 65, 81);
      pdf.setLineWidth(0.3);
      pdf.line(x + (i + 1) * slotW, y + 2, x + (i + 1) * slotW, y + h - 2);
    }
  });
}

/* ─── HORIZONTAL BAR CHART ───────────────────────────────── */
function drawHBar(pdf, data, x, y, w, barH = 5.5, gap = 2.5, labelW = 42) {
  if (!data || data.length === 0) {
    setFont(pdf, 'normal', 7, COL_GRAY3);
    pdf.text('No data for selected period', x + 4, y + 8);
    return y + 12;
  }
  const maxVal = Math.max(...data.map(d => d.value || 0), 1);
  const barAreaW = w - labelW - 14;

  data.forEach((item, i) => {
    const rowY = y + i * (barH + gap);
    const barW = Math.max(2, ((item.value || 0) / maxVal) * barAreaW);
    const [cr, cg, cb] = hexToRgb(item.color || PALETTE[i % PALETTE.length]);

    // Label
    setFont(pdf, 'normal', 6.5, COL_GRAY6);
    pdf.text(trunc(item.label, 22), x, rowY + barH - 1);

    // Track (bg)
    pdf.setFillColor(...COL_GRAY1);
    pdf.roundedRect(x + labelW, rowY, barAreaW, barH, 1, 1, 'F');

    // Fill
    pdf.setFillColor(cr, cg, cb);
    pdf.roundedRect(x + labelW, rowY, barW, barH, 1, 1, 'F');

    // Value label
    setFont(pdf, 'bold', 6.5, COL_GRAY5);
    pdf.text(String(item.value ?? 0), x + labelW + barAreaW + 2.5, rowY + barH - 1);
  });
  return y + data.length * (barH + gap);
}

/* ─── VERTICAL BAR CHART ─────────────────────────────────── */
function drawVBar(pdf, data, x, y, w, chartH, labelKey, valueKeys) {
  if (!data || data.length === 0) {
    setFont(pdf, 'normal', 7, COL_GRAY3);
    pdf.text('No data for selected period', x + 4, y + chartH / 2);
    return;
  }
  const bottomPad = 11;
  const topPad = 8; // legend area
  const usableH = chartH - bottomPad - topPad;
  const colors = ['#6ec7c8', '#c164d8', '#f5c542', '#ff66c4'];

  let maxVal = 1;
  data.forEach(d => valueKeys.forEach(k => { if ((d[k] || 0) > maxVal) maxVal = d[k] || 0; }));

  const groupW = w / data.length;
  const barW = Math.max(3, Math.min(8, (groupW * 0.65) / valueKeys.length));

  // Y-axis ghost lines
  [0, 0.25, 0.5, 0.75, 1].forEach(frac => {
    const lineY = y + topPad + usableH * (1 - frac);
    pdf.setDrawColor(...COL_GRAY2);
    pdf.setLineWidth(0.2);
    pdf.line(x, lineY, x + w, lineY);
    if (frac > 0) {
      setFont(pdf, 'normal', 5.5, COL_GRAY3);
      pdf.text(String(Math.round(maxVal * frac)), x - 1, lineY + 1, { align: 'right' });
    }
  });

  data.forEach((item, i) => {
    const groupX = x + i * groupW + groupW * 0.15;
    valueKeys.forEach((key, ki) => {
      const val = item[key] || 0;
      const bH = Math.max(1, (val / maxVal) * usableH);
      const bX = groupX + ki * (barW + 1.5);
      const bY = y + topPad + usableH - bH;
      const [cr, cg, cb] = hexToRgb(colors[ki % colors.length]);
      pdf.setFillColor(cr, cg, cb);
      pdf.roundedRect(bX, bY, barW, bH, 0.8, 0.8, 'F');

      // value label on top of bar
      if (val > 0) {
        setFont(pdf, 'bold', 4.5, COL_GRAY5);
        pdf.text(String(val), bX + barW / 2, bY - 0.8, { align: 'center' });
      }
    });

    // X label
    setFont(pdf, 'normal', 5, COL_GRAY4);
    const label = trunc(String(item[labelKey] || ''), 7);
    pdf.text(label, x + i * groupW + groupW / 2, y + topPad + usableH + 5, { align: 'center' });
  });

  // Legend
  valueKeys.forEach((key, ki) => {
    const [lr, lg, lb] = hexToRgb(colors[ki % colors.length]);
    pdf.setFillColor(lr, lg, lb);
    pdf.roundedRect(x + ki * 36, y + 1, 3.5, 3.5, 0.5, 0.5, 'F');
    setFont(pdf, 'normal', 5.5, COL_GRAY4);
    pdf.text(key.charAt(0).toUpperCase() + key.slice(1), x + ki * 36 + 5.5, y + 4.5);
  });
}

/* ─── PIE / DONUT CHART ──────────────────────────────────── */
function drawDonut(pdf, data, cx, cy, outerR, innerR = 0.45) {
  if (!data || data.length === 0) return;
  const total = data.reduce((s, d) => s + (d.value || d.count || 0), 0);
  if (total === 0) return;

  const internal = pdf.internal;
  const k = internal.scaleFactor;
  const pH = internal.pageSize.getHeight();

  let startAngle = -Math.PI / 2;

  data.forEach((item, i) => {
    const val = item.value || item.count || 0;
    if (val === 0) return;
    const slice = (val / total) * 2 * Math.PI;
    const [cr, cg, cb] = hexToRgb(item.color || PALETTE[i % PALETTE.length]);
    pdf.setFillColor(cr, cg, cb);

    const steps = Math.max(8, Math.round((slice / (2 * Math.PI)) * 40));
    const pts = [[cx, cy]];
    for (let s = 0; s <= steps; s++) {
      const a = startAngle + (s / steps) * slice;
      pts.push([cx + outerR * Math.cos(a), cy + outerR * Math.sin(a)]);
    }

    pdf.internal.write(
      pts.map((p, idx) => {
        const xi = (p[0] * k).toFixed(3);
        const yi = ((pH - p[1]) * k).toFixed(3);
        return idx === 0 ? `${xi} ${yi} m` : `${xi} ${yi} l`;
      }).join(' ') + ' f'
    );
    startAngle += slice;
  });

  // White inner circle (donut hole)
  pdf.setFillColor(...COL_WHITE);
  const innerR2 = outerR * innerR;
  const pts2 = [];
  for (let s = 0; s < 32; s++) {
    const a = (s / 32) * 2 * Math.PI;
    pts2.push([cx + innerR2 * Math.cos(a), cy + innerR2 * Math.sin(a)]);
  }
  pdf.internal.write(
    pts2.map((p, idx) => {
      const xi = (p[0] * k).toFixed(3);
      const yi = ((pH - p[1]) * k).toFixed(3);
      return idx === 0 ? `${xi} ${yi} m` : `${xi} ${yi} l`;
    }).join(' ') + ' f'
  );
}

/* ─── DATA TABLE ─────────────────────────────────────────── */
function drawTable(pdf, headers, rows, x, y, colWidths, rowH = 6) {
  const totalW = colWidths.reduce((a, b) => a + b, 0);

  // Header bg
  pdf.setFillColor(240, 253, 253);
  pdf.roundedRect(x, y, totalW, rowH, 1, 1, 'F');

  // Teal header bottom line
  pdf.setDrawColor(...COL_TEAL);
  pdf.setLineWidth(0.4);
  pdf.line(x, y + rowH, x + totalW, y + rowH);

  setFont(pdf, 'bold', 6, [0, 120, 120]);
  let cx = x;
  headers.forEach((h, i) => {
    pdf.text(h, cx + 2, y + rowH - 1.5);
    cx += colWidths[i];
  });

  // Rows
  rows.forEach((row, ri) => {
    const rowY = y + rowH * (ri + 1);

    if (ri % 2 === 0) {
      pdf.setFillColor(249, 250, 251);
      pdf.rect(x, rowY, totalW, rowH, 'F');
    }

    setFont(pdf, ri % 2 === 0 ? 'normal' : 'normal', 6, COL_GRAY6);
    cx = x;
    row.forEach((cell, ci) => {
      const cellStr = trunc(String(cell ?? '--'), Math.floor(colWidths[ci] / 1.8));
      pdf.text(cellStr, cx + 2, rowY + rowH - 1.5);
      cx += colWidths[ci];
    });

    // Subtle divider
    pdf.setDrawColor(240, 240, 240);
    pdf.setLineWidth(0.15);
    pdf.line(x, rowY + rowH, x + totalW, rowY + rowH);
  });

  // Outer border
  pdf.setDrawColor(...COL_GRAY2);
  pdf.setLineWidth(0.3);
  pdf.roundedRect(x, y, totalW, rowH * (rows.length + 1), 1, 1, 'S');

  return y + rowH * (rows.length + 1);
}

/* ─── PROGRESS BAR ROW ───────────────────────────────────── */
function drawProgressRow(pdf, label, value, maxVal, x, y, w, colorHex, unit = '') {
  const trackW = w - 48;
  const fillW = Math.max(1, (Math.min(value, maxVal) / maxVal) * trackW);
  const [cr, cg, cb] = hexToRgb(colorHex);

  setFont(pdf, 'normal', 6, COL_GRAY5);
  pdf.text(trunc(label, 20), x, y + 3.5);

  // Track
  pdf.setFillColor(...COL_GRAY2);
  pdf.roundedRect(x + 42, y, trackW, 4, 1, 1, 'F');

  // Fill
  pdf.setFillColor(cr, cg, cb);
  pdf.roundedRect(x + 42, y, fillW, 4, 1, 1, 'F');

  // Value text
  setFont(pdf, 'bold', 6, [cr, cg, cb]);
  pdf.text(`${value}${unit}`, x + w - 2, y + 3.5, { align: 'right' });
}

/* ─── STAR RATING ────────────────────────────────────────── */
function drawRating(pdf, rating, x, y, starPx = 3.5) {
  const filled = Math.round(rating);
  for (let s = 0; s < 5; s++) {
    pdf.setTextColor(...(s < filled ? COL_GOLD : COL_GRAY2));
    setFont(pdf, 'bold', starPx * 2.5, null);
    pdf.text('*', x + s * (starPx + 1), y);
  }
}


/* ═══════════════════════════════════════════════════════════
   MAIN EXPORT FUNCTION
═══════════════════════════════════════════════════════════ */
export async function exportAnalyticsPdf({
  analytics,
  filteredStaffList = [],
  roomAnalytics,
  ratingsAnalytics,
  filterSummaryText = 'All Data',
  fileName = 'Oleander_Analytics_Report.pdf'
}) {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const W = 210;
  const H = 297;
  const M = 8;          // margin
  const CW = W - M * 2; // content width = 194mm
  const TOP = 42;       // content starts after header
  const BOTTOM_SAFE = H - 14; // footer starts at ~283mm

  const generatedAt = new Date().toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });

  /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
     PAGE 1: OVERVIEW
  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
  drawHeader(pdf, 'Overview', filterSummaryText, 1, 5, generatedAt);

  let y = TOP;

  /* — 6 KPI chips — */
  const chipW = (CW - 5 * 2.5) / 6;
  const chipH = 18;
  const kpis = [
    { label: 'Total',     value: analytics.totalRequests,   acc: COL_DARK },
    { label: 'Completed', value: analytics.completedCount,  acc: [6, 95, 70] },
    { label: 'Pending',   value: analytics.pendingCount,    acc: [217, 119, 6] },
    { label: 'DND',       value: analytics.dndCount,        acc: [71, 85, 105] },
    { label: 'Critical',  value: analytics.criticalCount,   acc: [159, 18, 57] },
    { label: 'Dispute',   value: analytics.disputeCount,    acc: [107, 33, 168] },
  ];
  kpis.forEach((k, i) => {
    drawKpiChip(
      pdf, k.label, k.value,
      M + i * (chipW + 2.5), y, chipW, chipH,
      COL_GRAY1, k.acc
    );
  });
  y += chipH + 4;

  /* — Stats summary bar (NO EMOJI) — */
  const statsBarH = 12;
  drawStatsBar(pdf, [
    { label: 'Completion Rate',  value: `${analytics.completionRate?.toFixed(1) || 0}%`,    color: '#6ec7c8' },
    { label: 'Avg Turnaround',   value: fmtHours(analytics.avgCompletionHours),              color: '#f5c542' },
    { label: 'Median TAT',       value: fmtHours(analytics.medianCompletionHours),            color: '#c164d8' },
    { label: 'SLA 4h Rate',      value: `${analytics.sla4hRate?.toFixed(1) || 0}%`,          color: '#4ecdc4' },
  ], M, y, CW, statsBarH);
  y += statsBarH + 6;

  /* — Split: Category bars (left) + Status donut (right) — */
  const leftW  = CW * 0.56;
  const rightW = CW * 0.38;
  const rightX = M + leftW + 6;

  // Category chart
  y = drawSectionTitle(pdf, 'Requests by Category',
    `Top ${Math.min(8, (analytics.requestsByCategory || []).length)} categories by volume`, y);
  y += 2;
  const catData = (analytics.requestsByCategory || []).slice(0, 8).map(c => ({ label: c.category, value: c.count }));
  const catEndY = drawHBar(pdf, catData, M, y, leftW, 5.5, 2, 42);

  // Status donut
  const donutTitleY = y - 6;
  setFont(pdf, 'bold', 7.5, COL_DARK);
  pdf.text('Status Distribution', rightX, donutTitleY + 6);

  const pieData = (analytics.requestsByStatus || []).map(s => ({ label: s.name, value: s.count, color: s.color }));
  const donutCx = rightX + rightW / 2;
  const donutCy = donutTitleY + 26;
  drawDonut(pdf, pieData, donutCx, donutCy, 18);

  // Donut legend
  let legY = donutTitleY + 8;
  pieData.forEach((p, i) => {
    const [lr, lg, lb] = hexToRgb(p.color || PALETTE[i % PALETTE.length]);
    pdf.setFillColor(lr, lg, lb);
    pdf.roundedRect(rightX, legY, 3, 3, 0.5, 0.5, 'F');
    setFont(pdf, 'normal', 6, COL_GRAY5);
    pdf.text(`${trunc(p.label, 14)}: ${p.value}`, rightX + 5, legY + 2.5);
    legY += 5.5;
  });

  y = Math.max(catEndY, legY) + 6;

  /* — Trend chart — */
  if (y < BOTTOM_SAFE - 48) {
    y = drawSectionTitle(pdf, 'Daily Request Activity', 'Created vs Completed over the selected period', y);
    y += 2;
    const trendData = (analytics.trendData || []).slice(-16);
    drawVBar(pdf, trendData, M, y, CW, 36, 'label', ['created', 'completed']);
    y += 46;
  }

  drawFooter(pdf, `Operational Intelligence Report  |  Page 1 of 5`, filterSummaryText, H);


  /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
     PAGE 2: DEPARTMENT PERFORMANCE
  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
  pdf.addPage();
  drawHeader(pdf, 'Department Performance', filterSummaryText, 2, 5, generatedAt);
  y = TOP;

  /* — Volume vs Completion vertical bar (left) / TAT horizontal bar (right) — */
  const deptStats = analytics.departmentStats || [];
  const deptChartData = deptStats.slice(0, 9).map(d => ({
    department: trunc(d.department, 9),
    total: d.total,
    completed: d.completed
  }));

  const halfW = CW / 2 - 3;

  y = drawSectionTitle(pdf, 'Volume vs Completion by Department', 'Total requests and completions per department', y);
  y += 2;
  drawVBar(pdf, deptChartData, M, y, halfW, 36, 'department', ['total', 'completed']);

  setFont(pdf, 'bold', 7.5, COL_DARK);
  pdf.text('Avg Turnaround by Department', M + halfW + 6, y - 4);
  const tatData = deptStats.filter(d => d.avgCompletionMinutes > 0).slice(0, 9)
    .map(d => ({ label: trunc(d.department, 15), value: d.avgCompletionMinutes }));
  drawHBar(pdf, tatData, M + halfW + 6, y, halfW, 5, 2, 40);

  y += 42;

  /* — Department table — */
  if (y < BOTTOM_SAFE - 50) {
    y = drawSectionTitle(pdf, 'Department Performance Directory', 'Full stats for each department', y);
    y += 2;
    const dH = ['Department', 'Total', 'Done', 'Pending', 'DND', 'Critical', 'Dispute', 'Avg TAT', 'Rate'];
    const dW = [36, 14, 14, 15, 10, 14, 14, 18, 13];
    const dRows = deptStats.slice(0, 11).map(d => [
      trunc(d.department, 17), d.total, d.completed, d.pending,
      d.dnd, d.critical, d.dispute,
      fmtHours(d.avgCompletionHours), `${d.completionRate.toFixed(0)}%`
    ]);
    y = drawTable(pdf, dH, dRows, M, y, dW) + 5;
  }

  /* — Completion rate progress bars — */
  if (y < BOTTOM_SAFE - 50) {
    y = drawSectionTitle(pdf, 'Completion Rate by Department', 'Progress bars per department', y);
    y += 2;
    deptStats.slice(0, 10).forEach((d, i) => {
      if (y > BOTTOM_SAFE - 8) return;
      drawProgressRow(pdf, d.department, `${d.completionRate.toFixed(0)}`, 100,
        M, y, CW, PALETTE[i % PALETTE.length], '%');
      y += 6;
    });
  }

  drawFooter(pdf, `Operational Intelligence Report  |  Page 2 of 5`, filterSummaryText, H);


  /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
     PAGE 3: STAFF ANALYTICS
  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
  pdf.addPage();
  drawHeader(pdf, 'Staff Analytics', filterSummaryText, 3, 5, generatedAt);
  y = TOP;

  const staffList = filteredStaffList || [];

  /* — Staff 3 KPI summary — */
  const totalStaff = staffList.length;
  const topStaff = [...staffList].sort((a, b) => (b.completed || 0) - (a.completed || 0))[0];
  const ratedStaff = staffList.filter(x => x.avgRating !== null && x.avgRating !== undefined && x.avgRating > 0);
  const avgRatingAll = ratedStaff.length
    ? (ratedStaff.reduce((s, x) => s + x.avgRating, 0) / ratedStaff.length).toFixed(1)
    : '—';
  const s3kpis = [
    { label: 'Active Staff',    value: totalStaff,                    acc: [6, 95, 70] },
    { label: 'Top Performer',   value: trunc(topStaff?.name || '--', 12), acc: COL_DARK },
    { label: 'Avg Team CSAT',   value: avgRatingAll !== '—' ? `${avgRatingAll}/5.0` : '—', acc: [146, 64, 14] },
  ];
  const s3chipW = (CW - 2 * 2.5) / 3;
  s3kpis.forEach((k, i) => {
    drawKpiChip(pdf, k.label, k.value, M + i * (s3chipW + 2.5), y, s3chipW, 16, COL_GRAY1, k.acc);
  });
  y += 22;

  /* — Productivity bars (left) + TAT bars (right) — */
  y = drawSectionTitle(pdf, 'Staff Productivity (Completed Requests)', '', y);
  y += 2;
  const staffProd = staffList.slice(0, 10).map(s => ({ label: trunc(s.name, 16), value: s.completed || 0 }));
  const staffTat  = staffList.slice(0, 10).map(s => ({ label: trunc(s.name, 16), value: s.avgCompletionMinutes || 0 }));

  drawHBar(pdf, staffProd, M, y, halfW, 5, 2, 42);
  setFont(pdf, 'bold', 7.5, COL_DARK);
  pdf.text('Resolution Speed (Minutes)', M + halfW + 6, y - 2);
  drawHBar(pdf, staffTat, M + halfW + 6, y, halfW, 5, 2, 42);
  y += staffList.length > 0 ? Math.min(staffProd.length, 10) * 7.5 + 6 : 14;

  /* — Staff table — */
  if (y < BOTTOM_SAFE - 55) {
    y = drawSectionTitle(pdf, 'Staff Performance Directory', 'Individual stats for all staff', y);
    y += 2;
    const sH = ['Staff Name', 'Department', 'Completed', 'Speed (min)', 'CSAT'];
    const sW = [48, 42, 28, 28, 22];
    const sRows = staffList.slice(0, 15).map(s => [
      trunc(s.name, 22), trunc(s.department, 18),
      `${s.completed || 0} reqs`, `${s.avgCompletionMinutes || 0} min`,
      s.avgRating !== null && s.avgRating !== undefined ? `${s.avgRating} / 5.0` : '—'
    ]);
    y = drawTable(pdf, sH, sRows, M, y, sW) + 5;
  }

  /* — CSAT Rating progress bars — */
  if (y < BOTTOM_SAFE - 40 && ratedStaff.length > 0) {
    y = drawSectionTitle(pdf, 'Staff CSAT Rating Index', 'Star rating per team member (max 5)', y);
    y += 2;
    const maxCSAT = 5;
    ratedStaff.slice(0, 10).forEach((s, i) => {
      if (y > BOTTOM_SAFE - 8) return;
      drawProgressRow(pdf, s.name, (s.avgRating || 0).toFixed(1), maxCSAT,
        M, y, CW, '#f5c542', '/5.0');
      y += 6;
    });
  }

  drawFooter(pdf, `Operational Intelligence Report  |  Page 3 of 5`, filterSummaryText, H);


  /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
     PAGE 4: ROOM ANALYTICS
  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
  pdf.addPage();
  drawHeader(pdf, 'Room Analytics', filterSummaryText, 4, 5, generatedAt);
  y = TOP;

  /* — Room 5 KPI chips — */
  const rKipW = (CW - 4 * 2) / 5;
  const rKpis = [
    { label: 'Active Rooms',  value: roomAnalytics.activeRoomsCount || 0,                        acc: COL_DARK },
    { label: 'Avg Req/Room',  value: (roomAnalytics.avgRequestsPerRoom || 0).toFixed(1),          acc: [6, 95, 70] },
    { label: 'Top Room',      value: roomAnalytics.topDemandingRoom?.room_no || '--',             acc: [30, 64, 175] },
    { label: 'Slowest Room',  value: roomAnalytics.slowestRoom?.room_no || '--',                  acc: [146, 64, 14] },
    { label: 'Maint. Alerts', value: roomAnalytics.highMaintenanceCount || 0,                     acc: [159, 18, 57] },
  ];
  rKpis.forEach((k, i) => {
    drawKpiChip(pdf, k.label, k.value, M + i * (rKipW + 2), y, rKipW, 16, COL_GRAY1, k.acc);
  });
  y += 22;

  /* — Room volume (left) + TAT (right) — */
  y = drawSectionTitle(pdf, 'Top Active Rooms by Volume', 'Total requests per room', y);
  y += 2;
  const roomVol = (roomAnalytics.top10ActiveChart || []).slice(0, 10).map(r => ({
    label: `Rm ${r.room || r.room_no || '?'}`,
    value: (r.Completed || 0) + (r.Pending || 0)
  }));
  drawHBar(pdf, roomVol, M, y, halfW, 5, 2, 30);

  setFont(pdf, 'bold', 7.5, COL_DARK);
  pdf.text('Room Avg Turnaround (min)', M + halfW + 6, y - 2);
  const roomTat = (roomAnalytics.roomTATChart || []).slice(0, 8).map(r => ({
    label: `Rm ${r.room || r.room_no || '?'}`,
    value: r.avgMinutes || 0
  }));
  drawHBar(pdf, roomTat, M + halfW + 6, y, halfW, 5, 2, 30);
  y += Math.max(roomVol.length, roomTat.length) * 7.5 + 6;

  /* — Maintenance hotspots — */
  if ((roomAnalytics.maintenanceHotspotsChart || []).length > 0 && y < BOTTOM_SAFE - 35) {
    y = drawSectionTitle(pdf, 'Maintenance Hotspots', 'Rooms with highest maintenance call frequency', y);
    y += 2;
    const maintData = (roomAnalytics.maintenanceHotspotsChart || []).slice(0, 8).map(r => ({
      label: r.name, value: r.value
    }));
    drawHBar(pdf, maintData, M, y, CW * 0.5, 5, 2, 28);

    // Small donut for hotspots
    const donutCx2 = M + CW * 0.75;
    const donutCy2 = y + 22;
    drawDonut(pdf, maintData.map((m, i) => ({ ...m, color: PALETTE[i % PALETTE.length] })), donutCx2, donutCy2, 16);
    let mLegY = y + 4;
    maintData.slice(0, 6).forEach((m, i) => {
      const [lr, lg, lb] = hexToRgb(PALETTE[i % PALETTE.length]);
      pdf.setFillColor(lr, lg, lb);
      pdf.roundedRect(M + CW * 0.52, mLegY, 3, 3, 0.5, 0.5, 'F');
      setFont(pdf, 'normal', 6, COL_GRAY5);
      pdf.text(`${trunc(m.label, 12)}: ${m.value}`, M + CW * 0.52 + 4.5, mLegY + 2.5);
      mLegY += 5.5;
    });
    y += Math.max(maintData.length * 7.5, 45) + 4;
  }

  /* — Room breakdown table — */
  if (y < BOTTOM_SAFE - 45) {
    y = drawSectionTitle(pdf, 'Room Breakdown Directory', '', y);
    y += 2;
    const rH = ['Room', 'Total', 'Done', 'Pend.', 'Top Category', 'Avg TAT', 'Maint'];
    const rW = [18, 14, 14, 14, 52, 24, 16];
    const rRows = (roomAnalytics.roomList || []).slice(0, 13).map(r => [
      `Rm ${r.room_no}`, r.totalRequests, r.completedCount, r.pendingCount,
      trunc(r.topCategory, 22), fmtHours(r.avgCompletionHours),
      r.maintenanceCount > 0 ? `${r.maintenanceCount} !!` : '--'
    ]);
    drawTable(pdf, rH, rRows, M, y, rW);
  }

  drawFooter(pdf, `Operational Intelligence Report  |  Page 4 of 5`, filterSummaryText, H);


  /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
     PAGE 5: RATINGS & FEEDBACK
  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
  pdf.addPage();
  drawHeader(pdf, 'Ratings & Feedback', filterSummaryText, 5, 5, generatedAt);
  y = TOP;

  /* — 3 rating KPIs — */
  const r3chips = [
    { label: 'Overall Rating',    value: `${ratingsAnalytics.overallAvg} / 5.0`,       acc: [146, 64, 14]  },
    { label: 'Satisfaction Rate', value: `${ratingsAnalytics.positiveRate}%`,            acc: [6, 95, 70]    },
    { label: 'Total Reviews',     value: `${ratingsAnalytics.totalRated}`,               acc: [107, 33, 168] },
  ];
  const r3chipW = (CW - 2 * 2.5) / 3;
  r3chips.forEach((k, i) => {
    drawKpiChip(pdf, k.label, k.value, M + i * (r3chipW + 2.5), y, r3chipW, 16, COL_GRAY1, k.acc);
  });
  y += 22;

  /* — Overall star display — */
  setFont(pdf, 'bold', 8, COL_DARK);
  pdf.text('Guest Satisfaction Score:', M, y + 4);
  // Draw stars as text chars (no special unicode - just asterisks styled as gold)
  const starRating = Math.round(ratingsAnalytics.overallAvg || 0);
  for (let s = 0; s < 5; s++) {
    setFont(pdf, 'bold', 14, s < starRating ? COL_GOLD : COL_GRAY2);
    pdf.text('*', M + 56 + s * 8, y + 4.5);
  }
  setFont(pdf, 'normal', 7.5, COL_GRAY4);
  pdf.text(
    `(${ratingsAnalytics.overallAvg}/5.0 based on ${ratingsAnalytics.totalRated} rated service requests)`,
    M + 102, y + 4
  );
  y += 11;

  /* — Rating distribution (left) + Dept avg rating (right) — */
  y = drawSectionTitle(pdf, 'Rating Distribution by Star Tier', 'Count of reviews at each star level', y);
  y += 2;
  const distData = (ratingsAnalytics.ratingDistData || []).map((r, i) => ({
    label: r.star || `${5 - i} Stars`,
    value: r.count,
    color: ['#10b981', '#6ec7c8', '#f5c542', '#f97316', '#ef4444'][i]
  }));
  drawHBar(pdf, distData, M, y, halfW, 6, 2.5, 26);

  setFont(pdf, 'bold', 7.5, COL_DARK);
  pdf.text('Avg Rating by Department', M + halfW + 6, y - 2);
  const deptRatingData = (ratingsAnalytics.deptRatingsData || []).slice(0, 7).map(d => ({
    label: trunc(d.department, 14),
    value: d.avgRating,
    color: '#f5c542'
  }));
  drawHBar(pdf, deptRatingData, M + halfW + 6, y, halfW, 6, 2, 38);
  y += Math.max(distData.length * 8.5, deptRatingData.length * 8 + 8) + 6;

  /* — Guest feedback table — */
  if (y < BOTTOM_SAFE - 55) {
    y = drawSectionTitle(pdf, 'Recent Guest Feedback', 'Latest rated service requests and guest comments', y);
    y += 2;
    const fH = ['Room', 'Category', 'Rating', 'Guest Feedback / Details', 'Date'];
    const fW = [18, 35, 16, 96, 23];
    const fRows = (ratingsAnalytics.ratingsList || []).slice(0, 14).map(item => [
      `Rm ${item.room || '--'}`,
      trunc(item.category, 16),
      `${item.rating}/5.0`,
      trunc(item.feedback || item.details || 'Rating submitted by guest.', 48),
      fmtDate(item.createdAt || item.created_at)
    ]);
    y = drawTable(pdf, fH, fRows, M, y, fW) + 5;
  }

  /* — Satisfaction split — */
  if (y < BOTTOM_SAFE - 42) {
    y = drawSectionTitle(pdf, 'Satisfaction Breakdown', 'Positive (4-5 star) vs Neutral/Negative reviews', y);
    y += 4;
    const posCount = (distData[0]?.value || 0) + (distData[1]?.value || 0);
    const negCount = (distData[3]?.value || 0) + (distData[4]?.value || 0);
    const neuCount = distData[2]?.value || 0;
    const total = ratingsAnalytics.totalRated || 1;

    [
      { label: 'Positive (4-5 Stars)', count: posCount, color: '#10b981' },
      { label: 'Neutral (3 Stars)',     count: neuCount, color: '#f5c542' },
      { label: 'Negative (1-2 Stars)', count: negCount, color: '#ef4444' },
    ].forEach((seg) => {
      if (y > BOTTOM_SAFE - 8) return;
      drawProgressRow(pdf, seg.label, seg.count, total, M, y, CW, seg.color, ' reviews');
      y += 7;
    });
  }

  drawFooter(pdf, `Operational Intelligence Report  |  Page 5 of 5`, filterSummaryText, H);

  /* ─── Save PDF ─────────────────────────────────────────── */
  pdf.save(fileName);
  return true;
}

/* ═══════════════════════════════════════════════════════════
   SERVICE REQUESTS PDF EXPORT (Landscape Detailed Report)
═══════════════════════════════════════════════════════════ */
function calculateTatStr(req) {
  const compIso = req.completed_at || req.metadata?.completed_at || req.metadata?.completed_by_staff_at || req.metadata?.completion_response_at;
  const startIso = req.created_at;
  if (!compIso || !startIso) return '—';
  try {
    const diffMs = new Date(compIso).getTime() - new Date(startIso).getTime();
    if (diffMs <= 0) return '0 min';
    const totalMinutes = Math.round(diffMs / 60000);
    if (totalMinutes < 60) return `${totalMinutes} min`;
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  } catch {
    return '—';
  }
}

export async function exportServiceRequestsPdf({
  requests = [],
  filterContext = {},
  fileName = 'Service_Requests_Report.pdf'
}) {
  const pdf = new jsPDF('l', 'mm', 'a4');
  const W = 297;
  const H = 210;
  const M = 8;
  const CW = W - M * 2; // 281mm
  const BOTTOM_SAFE = H - 12;

  const generatedAt = new Date().toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });

  const deptRaw = filterContext.department || 'All Departments';
  const deptDisplay = (deptRaw === 'all' || !deptRaw) ? 'ALL DEPARTMENTS' : String(deptRaw).toUpperCase();
  const viewRaw = String(filterContext.view || 'all').toLowerCase();
  const outcomeRaw = String(filterContext.outcome || 'all').toLowerCase();
  const dateRangeText = filterContext.dateRangeText || 'All Available Dates';

  let mainTitle = `SERVICE REQUESTS REGISTER — ${deptDisplay}`;
  let badgeText = 'ALL REQUESTS';
  let badgeColor = COL_TEAL;

  if (viewRaw === 'dispute' || outcomeRaw === 'dispute') {
    mainTitle = `SERVICE DISPUTE REQUESTS — ${deptDisplay}`;
    badgeText = 'SERVICE DISPUTE';
    badgeColor = [124, 58, 237]; // Purple
  } else if (viewRaw === 'cancelled') {
    mainTitle = `CANCELLED SERVICE REQUESTS — ${deptDisplay}`;
    badgeText = 'CANCELLED';
    badgeColor = [225, 29, 72]; // Rose
  } else if (outcomeRaw === 'critical' || viewRaw === 'critical') {
    mainTitle = `CRITICAL FIX REQUESTS — ${deptDisplay}`;
    badgeText = 'CRITICAL FIX';
    badgeColor = [220, 38, 38]; // Red
  } else if (outcomeRaw === 'dnd' || viewRaw === 'dnd') {
    mainTitle = `DND (DO NOT DISTURB) REQUESTS — ${deptDisplay}`;
    badgeText = 'DND NOTIFIED';
    badgeColor = [217, 119, 6]; // Amber
  } else if (viewRaw === 'pending') {
    mainTitle = `PENDING SERVICE REQUESTS — ${deptDisplay}`;
    badgeText = 'PENDING';
    badgeColor = [217, 119, 6]; // Amber
  } else if (viewRaw === 'completed') {
    mainTitle = `COMPLETED SERVICE REQUESTS — ${deptDisplay}`;
    badgeText = 'COMPLETED';
    badgeColor = [5, 150, 105]; // Emerald
  }

  const filterSummary = `Dept: ${deptDisplay}  |  View: ${filterContext.viewLabel || filterContext.view || 'All'}  |  Period: ${dateRangeText}  |  Records: ${requests.length}`;

  function drawLandscapeHeaderBlock(pNum) {
    const hTop = 6;
    const hH = 26;

    // Dark container
    pdf.setFillColor(...COL_DARK);
    pdf.roundedRect(M, hTop, CW, hH, 2.5, 2.5, 'F');

    // Left teal bar
    pdf.setFillColor(...COL_TEAL);
    pdf.roundedRect(M, hTop, 3.5, hH, 1.5, 1.5, 'F');
    pdf.rect(M + 1.5, hTop, 2, hH, 'F');

    // Brand title
    setFont(pdf, 'bold', 11, COL_WHITE);
    pdf.text('OLEANDER FARMS', M + 7.5, hTop + 7.5);

    // Document Main Title
    setFont(pdf, 'bold', 8.5, COL_TEAL);
    pdf.text(trunc(mainTitle, 65), M + 7.5, hTop + 13);

    // Separator line
    pdf.setDrawColor(...COL_GRAY4);
    pdf.setLineWidth(0.2);
    pdf.line(M + 7, hTop + 15.5, W - M - 68, hTop + 15.5);

    // Filter subtitle
    setFont(pdf, 'bold', 6.5, COL_TEAL);
    pdf.text('FILTER:', M + 7.5, hTop + 20.5);
    setFont(pdf, 'normal', 6.5, [180, 220, 220]);
    pdf.text(trunc(filterSummary, 95), M + 20, hTop + 20.5);

    // Badge Pill
    const bW = 60;
    const bX = W - M - bW - 4;
    pdf.setFillColor(...badgeColor);
    pdf.roundedRect(bX, hTop + 4, bW, 8.5, 2, 2, 'F');
    setFont(pdf, 'bold', 7, COL_WHITE);
    pdf.text(badgeText, bX + bW / 2, hTop + 9.5, { align: 'center' });

    // Generated at
    setFont(pdf, 'normal', 6, COL_GRAY3);
    pdf.text(`Generated: ${generatedAt}`, bX + bW / 2, hTop + 16, { align: 'center' });
    setFont(pdf, 'normal', 5.5, COL_GRAY4);
    pdf.text(`Page ${pNum}`, bX + bW / 2, hTop + 21, { align: 'center' });
  }

  function drawLandscapeTableHeader(startY) {
    const rowH = 6.5;
    pdf.setFillColor(240, 253, 253);
    pdf.roundedRect(M, startY, CW, rowH, 1, 1, 'F');

    pdf.setDrawColor(...COL_TEAL);
    pdf.setLineWidth(0.4);
    pdf.line(M, startY + rowH, M + CW, startY + rowH);

    setFont(pdf, 'bold', 6.2, [0, 110, 110]);
    const headers = [
      { label: '#ID & CREATED', w: 28 },
      { label: 'ROOM & GUEST',  w: 34 },
      { label: 'DEPARTMENT',    w: 32 },
      { label: 'STATUS / OUTCOME', w: 30 },
      { label: 'REQUEST DETAILS', w: 68 },
      { label: 'WORK DONE & TAT', w: 39 },
      { label: 'DISPUTE / CANCEL NOTE & AUTHOR', w: 50 },
    ];

    let cx = M;
    headers.forEach((h) => {
      pdf.text(h.label, cx + 1.5, startY + rowH - 2);
      cx += h.w;
    });

    return startY + rowH;
  }

  // Draw Page 1
  drawLandscapeHeaderBlock(1);
  let y = 35;

  // KPI summary strip on Page 1
  const disputeCount = requests.filter(r => r.dispatch_status === 'guest_disputed' || r.metadata?.dispatch_status === 'guest_disputed' || r.status === 'dispute').length;
  const completedCount = requests.filter(r => r.status === 'completed' || r.completion_response || r.metadata?.completion_response).length;
  const pendingCount = requests.filter(r => ['pending', 'received', 'in-progress'].includes(r.status) && r.dispatch_status !== 'guest_disputed').length;
  const cancelledCount = requests.filter(r => r.status === 'cancelled').length;

  const kpis = [
    { label: 'TOTAL REQUESTS', val: requests.length, color: COL_DARK },
    { label: 'SERVICE DISPUTES', val: disputeCount, color: [124, 58, 237] },
    { label: 'COMPLETED', val: completedCount, color: [5, 150, 105] },
    { label: 'PENDING', val: pendingCount, color: [217, 119, 6] },
    { label: 'CANCELLED', val: cancelledCount, color: [225, 29, 72] }
  ];

  const chipW = (CW - 4 * 2.5) / 5;
  const chipH = 10;
  kpis.forEach((k, i) => {
    const cx = M + i * (chipW + 2.5);
    pdf.setFillColor(249, 250, 251);
    pdf.roundedRect(cx, y, chipW, chipH, 1.5, 1.5, 'F');
    pdf.setDrawColor(...COL_GRAY2);
    pdf.setLineWidth(0.2);
    pdf.roundedRect(cx, y, chipW, chipH, 1.5, 1.5, 'S');

    // Colored left pip
    pdf.setFillColor(...k.color);
    pdf.roundedRect(cx + 1.5, y + 2, 2, chipH - 4, 0.5, 0.5, 'F');

    setFont(pdf, 'normal', 5.5, COL_GRAY5);
    pdf.text(k.label, cx + 5, y + 4);
    setFont(pdf, 'bold', 8, COL_DARK);
    pdf.text(String(k.val), cx + 5, y + 8.5);
  });

  y += chipH + 3.5;

  // Table header
  y = drawLandscapeTableHeader(y);

  if (!requests.length) {
    pdf.setFillColor(249, 250, 251);
    pdf.rect(M, y, CW, 18, 'F');
    setFont(pdf, 'normal', 8, COL_GRAY4);
    pdf.text('No service requests found for the selected filter criteria.', M + CW / 2, y + 10, { align: 'center' });
    y += 18;
  } else {
    const colWidths = [28, 34, 32, 30, 68, 39, 50];

    requests.forEach((req, idx) => {
      // 1. Prepare strings for each column
      const reqIdStr = String(req.id || '').replace(/-/g, '').slice(0, 6).toUpperCase();
      const createdDate = req.created_at ? fmtDate(req.created_at) : '--';
      let createdTime = '';
      try {
        if (req.created_at) {
          const d = new Date(req.created_at);
          createdTime = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true });
        }
      } catch { createdTime = ''; }

      const idText = `#${reqIdStr}\n${createdDate}${createdTime ? ' ' + createdTime : ''}`;

      const roomStr = req.room_no ? `Room ${req.room_no}` : (req.metadata?.request_room_no ? `Room ${req.metadata.request_room_no}` : 'Room N/A');
      const guestStr = req.guest_name || req.guests?.name || 'Guest';
      const phoneStr = req.phone || req.guests?.phone || '';
      const guestText = `${roomStr}\n${guestStr}${phoneStr ? '\n' + phoneStr : ''}`;

      const categoryStr = req.category || 'General';
      const typeStr = req.service_type || req.metadata?.requested_activity || '';
      const deptText = `${categoryStr}${typeStr ? '\n(' + typeStr + ')' : ''}`;

      // Status text & badge
      const isDispute = req.dispatch_status === 'guest_disputed' || req.metadata?.dispatch_status === 'guest_disputed' || req.status === 'dispute';
      const isCancelled = req.status === 'cancelled';
      const isCritical = (req.completion_response === 'critical' || req.metadata?.completion_response === 'critical') && req.metadata?.critical_fix_status !== 'cancelled';
      const isDnd = req.completion_response === 'dnd' || req.metadata?.completion_response === 'dnd';
      const isComp = req.status === 'completed' || req.completion_response === 'yes';

      let statusBadge = 'Pending';
      if (isDispute) statusBadge = 'Dispute';
      else if (isCancelled) statusBadge = 'Cancelled';
      else if (isCritical) statusBadge = 'Critical Fix';
      else if (isDnd) statusBadge = 'DND';
      else if (isComp) statusBadge = 'Completed';

      const ratingVal = req.service_rating || req.metadata?.service_rating;
      const statusText = `${statusBadge}${req.dispatch_status && req.dispatch_status !== 'pending' ? '\n' + req.dispatch_status : ''}${ratingVal ? '\nRating: ' + ratingVal + '/5' : ''}`;

      // Details
      let detailsRaw = String(req.details || 'No details provided').trim();
      if (req.metadata?.emergency_type) {
        detailsRaw = `[${String(req.metadata.emergency_type).toUpperCase()} EMERGENCY] ${detailsRaw}`;
      }
      const detailsText = detailsRaw;

      // Handled By / TAT
      const staffName = req.metadata?.flow_work_done_by || req.metadata?.completed_by_staff_name || req.metadata?.assigned_staff_name || '—';
      const remarks = req.metadata?.flow_remarks || req.completion_remarks || '';
      const tat = calculateTatStr(req);
      const staffText = `Staff: ${staffName}${tat !== '—' ? '\nTAT: ' + tat : ''}${remarks ? '\nRemarks: ' + remarks : ''}`;

      // Dispute / Cancellation Note
      let noteText = '—';
      if (isDispute) {
        if (req.metadata?.dispute_note) {
          const author = req.metadata.dispute_note_by_user_name || 'Staff';
          const email = req.metadata.dispute_note_by_user_email ? ` (${req.metadata.dispute_note_by_user_email})` : '';
          const at = req.metadata.dispute_note_at ? fmtDate(req.metadata.dispute_note_at) : '';
          noteText = `Note: "${req.metadata.dispute_note}"\nBy: ${author}${email}${at ? ' on ' + at : ''}`;
        } else {
          noteText = 'Dispute: No note recorded yet';
        }
      } else if (isCancelled) {
        const reason = req.metadata?.cancellation_reason || '—';
        const by = req.metadata?.cancelled_by_user_name || 'Staff';
        const at = req.metadata?.cancelled_at ? fmtDate(req.metadata.cancelled_at) : '';
        noteText = `Reason: "${reason}"\nBy: ${by}${at ? ' on ' + at : ''}`;
      } else if (req.metadata?.dispute_note) {
        noteText = `Note: "${req.metadata.dispute_note}"`;
      }

      // 2. Measure wrapped lines
      setFont(pdf, 'normal', 5.8);
      const idLines = pdf.splitTextToSize(idText, colWidths[0] - 2);
      const guestLines = pdf.splitTextToSize(guestText, colWidths[1] - 2);
      const deptLines = pdf.splitTextToSize(deptText, colWidths[2] - 2);
      const statusLines = pdf.splitTextToSize(statusText, colWidths[3] - 2);
      const detailsLines = pdf.splitTextToSize(detailsText, colWidths[4] - 2);
      const staffLines = pdf.splitTextToSize(staffText, colWidths[5] - 2);
      const noteLines = pdf.splitTextToSize(noteText, colWidths[6] - 2);

      const maxLines = Math.max(
        idLines.length,
        guestLines.length,
        deptLines.length,
        statusLines.length,
        detailsLines.length,
        staffLines.length,
        noteLines.length,
        2
      );

      const lineStep = 3.1;
      const rowH = Math.max(8.5, maxLines * lineStep + 3);

      // Check page break
      if (y + rowH > BOTTOM_SAFE) {
        pdf.addPage('l', 'mm', 'a4');
        const curPage = pdf.internal.getCurrentPageInfo().pageNumber;
        drawLandscapeHeaderBlock(curPage);
        y = 35;
        y = drawLandscapeTableHeader(y);
      }

      // Draw row background
      if (idx % 2 === 0) {
        pdf.setFillColor(252, 252, 253);
        pdf.rect(M, y, CW, rowH, 'F');
      }

      // Highlight dispute rows with soft lavender indicator
      if (isDispute) {
        pdf.setFillColor(243, 232, 255);
        pdf.rect(M, y, 2.5, rowH, 'F');
      }

      // Render columns
      let cx = M;

      // Col 1: ID & Created
      setFont(pdf, 'bold', 5.8, COL_DARK);
      let ly = y + 3;
      idLines.forEach((l, li) => {
        if (li > 0) setFont(pdf, 'normal', 5.3, COL_GRAY5);
        pdf.text(l, cx + 1.5, ly);
        ly += lineStep;
      });
      cx += colWidths[0];

      // Col 2: Room & Guest
      setFont(pdf, 'bold', 5.8, [14, 116, 144]);
      ly = y + 3;
      guestLines.forEach((l, li) => {
        if (li === 1) setFont(pdf, 'normal', 5.6, COL_DARK);
        else if (li > 1) setFont(pdf, 'normal', 5, COL_GRAY4);
        pdf.text(l, cx + 1.5, ly);
        ly += lineStep;
      });
      cx += colWidths[1];

      // Col 3: Department
      setFont(pdf, 'bold', 5.6, COL_DARK);
      ly = y + 3;
      deptLines.forEach((l, li) => {
        if (li > 0) setFont(pdf, 'normal', 5.2, COL_GRAY5);
        pdf.text(l, cx + 1.5, ly);
        ly += lineStep;
      });
      cx += colWidths[2];

      // Col 4: Status badge pill
      let sBg = [243, 244, 246];
      let sFg = COL_DARK;
      if (isDispute) { sBg = [243, 232, 255]; sFg = [124, 58, 237]; }
      else if (isCancelled) { sBg = [255, 228, 230]; sFg = [225, 29, 72]; }
      else if (isCritical) { sBg = [254, 226, 226]; sFg = [220, 38, 38]; }
      else if (isDnd) { sBg = [254, 243, 199]; sFg = [180, 83, 9]; }
      else if (isComp) { sBg = [236, 253, 245]; sFg = [5, 150, 105]; }

      pdf.setFillColor(...sBg);
      pdf.roundedRect(cx + 1.5, y + 1.5, 24, 4.5, 1, 1, 'F');
      setFont(pdf, 'bold', 5.2, sFg);
      pdf.text(statusBadge, cx + 13.5, y + 4.6, { align: 'center' });

      if (statusLines.length > 1) {
        setFont(pdf, 'normal', 5, COL_GRAY5);
        let sly = y + 8.5;
        statusLines.slice(1).forEach((l) => {
          pdf.text(l, cx + 1.5, sly);
          sly += lineStep;
        });
      }
      cx += colWidths[3];

      // Col 5: Request Details
      setFont(pdf, 'normal', 5.6, COL_DARK);
      ly = y + 3;
      detailsLines.forEach((l) => {
        pdf.text(l, cx + 1.5, ly);
        ly += lineStep;
      });
      cx += colWidths[4];

      // Col 6: Handled By & TAT
      setFont(pdf, 'normal', 5.3, COL_GRAY6);
      ly = y + 3;
      staffLines.forEach((l, li) => {
        if (li === 0) setFont(pdf, 'bold', 5.4, COL_DARK);
        else setFont(pdf, 'normal', 5.2, COL_GRAY5);
        pdf.text(l, cx + 1.5, ly);
        ly += lineStep;
      });
      cx += colWidths[5];

      // Col 7: Dispute / Cancel Note
      if (isDispute && req.metadata?.dispute_note) {
        setFont(pdf, 'bold', 5.4, [124, 58, 237]);
      } else if (isCancelled) {
        setFont(pdf, 'bold', 5.4, [225, 29, 72]);
      } else {
        setFont(pdf, 'normal', 5.2, COL_GRAY5);
      }
      ly = y + 3;
      noteLines.forEach((l, li) => {
        if (li === 0 && (isDispute || isCancelled)) {
          // Keep bold color for first line
        } else {
          setFont(pdf, 'normal', 5, COL_GRAY6);
        }
        pdf.text(l, cx + 1.5, ly);
        ly += lineStep;
      });

      // Bottom border line for row
      pdf.setDrawColor(235, 238, 242);
      pdf.setLineWidth(0.18);
      pdf.line(M, y + rowH, M + CW, y + rowH);

      y += rowH;
    });
  }

  // Draw running footer across all pages
  const totalPages = pdf.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    pdf.setPage(p);
    const footY = H - 6;

    pdf.setFillColor(248, 250, 252);
    pdf.rect(M, footY - 3, CW, 5.5, 'F');
    pdf.setDrawColor(...COL_GRAY2);
    pdf.setLineWidth(0.2);
    pdf.line(M, footY - 3, M + CW, footY - 3);

    setFont(pdf, 'normal', 5.8, COL_GRAY4);
    pdf.text('Oleander Farms Resort & Spa · Operational Register', M + 2, footY + 0.5);

    setFont(pdf, 'normal', 5.8, COL_GRAY5);
    pdf.text(trunc(mainTitle, 60), W / 2, footY + 0.5, { align: 'center' });

    setFont(pdf, 'bold', 5.8, COL_TEAL);
    pdf.text(`Page ${p} of ${totalPages}`, W - M - 2, footY + 0.5, { align: 'right' });
  }

  pdf.save(fileName);
  return true;
}

/* ─── LEGACY STUBS ─────────────────────────────────────── */
export async function exportElementAsPdf() {
  throw new Error('Deprecated – use exportAnalyticsPdf instead.');
}
export async function exportMultiplePagesAsPdf() {
  throw new Error('Deprecated – use exportAnalyticsPdf instead.');
}
